import { useState, useEffect, useMemo } from "react";
import { Table, Input, Row, Col, Button, Popconfirm, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { getClientBrands, deleteClientBrand } from "@/ts/retrieve-data";
import { IBrand, BrandTableColumns, BrandsData } from "@/ts/common-types";
import { showToast } from "@/ts/modals";
import dayjs from "dayjs";
import { debounce } from "underscore";
import * as Sentry from "@sentry/react";
import AddBrandModal from "@components/modals/AddBrandModal";
import {
    DEFAULT_PAGE_SIZE,
    DEBOUNCE_DELAY,
    FIRST_PAGE,
    DEFAULT_PAGE_SIZE_OPTIONS,
    DateTimeFormats,
} from "@/ts/constants";
import { AiOutlineDelete } from "react-icons/ai";
import { usePermissionCheck, useUserTimezone } from "@/hooks/AllCustomHooks";
import { convertIsoDate } from "@/ts/utils";

interface ClientBrandsProps {
    clientId: number;
    timezone?: string;
}

const INITIAL_BRANDS_DATA: BrandsData = {
    loading: true,
    brands: [],
    pagination: {
        current: FIRST_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
        total: 0,
    },
    searchQuery: "",
};

const ClientBrands: React.FC<ClientBrandsProps> = ({ clientId, timezone }) => {
    const [brandsData, setBrandsData] =
        useState<BrandsData>(INITIAL_BRANDS_DATA);
    const [modalVisible, setModalVisible] = useState(false);
    const { canCreateClient, canDeleteClient } = usePermissionCheck();

    const currentTimeZone = useUserTimezone(timezone);

    const columns: ColumnsType<IBrand> = [
        {
            title: "ID",
            dataIndex: BrandTableColumns.ID,
            width: 100,
            sorter: (a, b) => a.id - b.id,
        },
        {
            title: "Brand Name",
            dataIndex: BrandTableColumns.NAME,
            width: "30%",
            ellipsis: true,
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: "Registered By",
            dataIndex: BrandTableColumns.REGISTERED_BY,
            width: "30%",
            ellipsis: true,
            sorter: (a, b) =>
                a.sys_registered_by.localeCompare(b.sys_registered_by),
        },
        {
            title: "Last Updated",
            dataIndex: BrandTableColumns.LAST_UPDATED,
            width: "25%",
            render: (date) => (
                <div>
                    <div>{convertIsoDate(String(date), currentTimeZone)}</div>
                    <div className="small text-muted">
                        {convertIsoDate(String(date))}{" "}
                        {DateTimeFormats.UTC_SUFFIX}
                    </div>
                </div>
            ),
            sorter: (a, b) =>
                dayjs(a.sys_last_updated).unix() -
                dayjs(b.sys_last_updated).unix(),
        },
        ...(canDeleteClient
            ? [
                  {
                      title: "Actions",
                      key: "actions",
                      width: 100,
                      render: (_: undefined, record: IBrand) => (
                          <div className="d-flex align-items-center gap-2">
                              <Popconfirm
                                  title="Delete Brand"
                                  description="Are you sure you want to delete this brand?"
                                  onConfirm={() => handleDeleteBrand(record.id)}
                                  okText="Yes"
                                  cancelText="No"
                                  okButtonProps={{
                                      danger: true,
                                      className: "ms-2",
                                  }}
                                  overlayClassName="p-3"
                                  styles={{ body: { padding: "12px" } }}
                              >
                                  <Tooltip title="Delete Brand">
                                      <Button
                                          type="text"
                                          danger
                                          className="p-0 btn-sm cursor-pointer"
                                          icon={
                                              <AiOutlineDelete className="fs-5" />
                                          }
                                      />
                                  </Tooltip>
                              </Popconfirm>
                          </div>
                      ),
                  },
              ]
            : []),
    ];

    const fetchBrands = async (
        page: number,
        pageSize: number,
        search: string
    ) => {
        setBrandsData((prev) => ({ ...prev, loading: true }));
        try {
            const result = await getClientBrands(
                clientId,
                page,
                pageSize,
                search
            );
            setBrandsData((prev) => ({
                ...prev,
                brands: result.brands,
                pagination: {
                    current: result.page,
                    pageSize: result.pageSize,
                    total: result.total,
                },
                loading: false,
            }));
        } catch (error: any) {
            console.error("Error fetching brands:", error);
            showToast("danger", error.message || "Failed to fetch brands");
            Sentry.captureException(error);
            setBrandsData((prev) => ({ ...prev, loading: false }));
        }
    };

    const handleSearch = (value: string) => {
        setBrandsData((prev) => ({
            ...prev,
            searchQuery: value,
            pagination: { ...prev.pagination, current: 1 },
        }));
        fetchBrands(FIRST_PAGE, brandsData.pagination.pageSize, value);
    };

    const debouncedSearch = useMemo(
        () => debounce(handleSearch, DEBOUNCE_DELAY),
        []
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    useEffect(() => {
        fetchBrands(FIRST_PAGE, DEFAULT_PAGE_SIZE, "");
    }, [clientId]);

    const handleDeleteBrand = async (brandId: number) => {
        try {
            await deleteClientBrand(clientId, brandId);
            showToast("success", "Brand deleted successfully");
            fetchBrands(
                brandsData.pagination.current,
                brandsData.pagination.pageSize,
                brandsData.searchQuery
            );
        } catch (error: any) {
            console.error("Error deleting brand:", error);
            showToast("danger", error.message || "Failed to delete brand");
            Sentry.captureException(error);
        }
    };

    return (
        <div>
            <Row className="mb-4" align="middle">
                <Col span={12}>
                    <h5 className="m-0">
                        All Brands{" "}
                        <span className="text-muted">
                            ({brandsData.pagination.total || 0})
                        </span>
                    </h5>
                </Col>
                <Col span={12} className="text-end">
                    <Input
                        placeholder="Search brands..."
                        className="border"
                        style={{ width: "40%" }}
                        defaultValue={brandsData.searchQuery}
                        onChange={(e) => {
                            const value = e.target.value.trim();
                            debouncedSearch(value);
                        }}
                        allowClear
                    />
                    {canCreateClient && (
                        <Button
                            type="primary"
                            className="ms-3"
                            onClick={() => setModalVisible(true)}
                        >
                            Add Brand
                        </Button>
                    )}
                </Col>
            </Row>

            <div className="mt-4">
                <Table
                    columns={columns}
                    dataSource={brandsData.brands}
                    rowKey="id"
                    loading={brandsData.loading}
                    pagination={{
                        ...brandsData.pagination,
                        onChange: (page, pageSize) => {
                            fetchBrands(page, pageSize, brandsData.searchQuery);
                        },
                        onShowSizeChange: (current, size) => {
                            setBrandsData((prev) => ({
                                ...prev,
                                pagination: {
                                    ...prev.pagination,
                                    current: FIRST_PAGE,
                                    pageSize: size,
                                },
                            }));
                            fetchBrands(
                                FIRST_PAGE,
                                size,
                                brandsData.searchQuery
                            );
                        },
                        showSizeChanger: true,
                        pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                        className: "mt-3",
                    }}
                    scroll={{ x: 1000, y: "calc(100vh - 350px)" }}
                    className="min-w-100 overflow-auto bg-white rounded"
                />
            </div>

            <AddBrandModal
                clientId={clientId}
                visible={modalVisible}
                onSuccess={() => {
                    setModalVisible(false);
                    fetchBrands(
                        FIRST_PAGE,
                        brandsData.pagination.pageSize,
                        brandsData.searchQuery
                    );
                }}
                onCancel={() => setModalVisible(false)}
            />
        </div>
    );
};

export default ClientBrands;
